// src/assets/data/cursos/finanzas-personales/modulo-6.js
import banner from "../../../courses/module-1/img6.png";

const modulo6 = {
  courseId: "finanzas-personales",
  id: "modulo-6",
  title: "📘 MÓDULO 6: Consumo Responsable y Hábitos Financieros",
  estimatedDuration: "2 horas",
  format: "Lectura + autoobservación + desafío escrito",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objetivos",
      title: "🎯 Objetivos del módulo",
      content: [
        "Identificar cómo y por qué tomamos decisiones de consumo sin pensar.",
        "Reconocer el efecto acumulado de los gastos pequeños y repetitivos.",
        "Desarrollar hábitos financieros conscientes y sostenibles.",
      ],
    },
    {
      id: "lectura",
      navLabel: "Lectura principal",
      title: "📖 Lectura principal: ¿Qué es el consumo responsable?",
      content: [
        "Consumir responsablemente no es dejar de comprar, sino hacerlo con conciencia y propósito.",
        "Se trata de preguntarte:",
        {
          type: "list",
          items: [
            "¿Realmente necesito esto?",
            "¿Puedo pagarlo sin endeudarme o afectar mis metas?",
            "¿Estoy comprando por impulso, presión o aburrimiento?",
          ],
        },
        "Consumir con intención = cuidar tu dinero y tus decisiones.",
      ],
    },
    {
      id: "razones",
      navLabel: "Razones",
      title: "🚩 Razones para adoptar el consumo consciente",
      content: [
        {
          type: "list",
          items: [
            "Evitas el remordimiento post-compra",
            "Reduces el desperdicio y el consumo por moda",
            "Fortaleces tu control financiero sin sentir que “te estás privando”",
            "Tomas decisiones alineadas con tus valores",
          ],
        },
      ],
    },
    {
      id: "gasto-hormiga",
      navLabel: "Gasto hormiga",
      title: "💸 El impacto del gasto hormiga",
      content: [
        "Los gastos hormiga son pequeños gastos diarios que parecen inofensivos, pero que se acumulan silenciosamente. Ejemplos:",
        {
          type: "table",
          headers: ["Gasto", "Gasto mensual aprox.", "Gasto anual aprox."],
          rows: [
            ["Café de $40", "$800", "$9,600"],
            ["Snacks de $30", "$600", "$7,200"],
            ["Apps de delivery", "$1,000", "$13,000"],
          ],
        },
        "Ejercicio rápido:",
        {
          type: "list",
          items: [
            "Anota durante 3 días TODO lo que gastas sin pensar.",
            "Al final, suma e identifica cuáles podrías reducir sin dolor.",
          ],
        },
      ],
    },
    {
      id: "tecnicas",
      navLabel: "Técnicas",
      title: "🛠 Técnicas para mejorar tus hábitos con el dinero",
      content: [
        {
          type: "orderedList",
          items: [
            "Regla de los 10 segundos: Antes de comprar, cuenta hasta 10 y pregúntate si lo necesitas.",
            "Lista de espera de 72 horas: Si no es urgente, espérate 3 días. Si aún lo quieres, cómpralo con intención.",
            "Presupuesto de gustitos: No se trata de eliminar placeres, sino de definir cuánto puedes darte sin afectar tus metas.",
            "Apps para rastrear hábitos: TryHabit, Tally, Google Sheets simple.",
            "Técnica del billete visual: Pon tu meta de ahorro escrita dentro de tu billetera o cartera.",
          ],
        },
      ],
    },
    {
      id: "herramienta",
      navLabel: "Herramienta",
      title:
        "🧩 Herramienta del módulo: hoja de seguimiento de hábitos financieros",
      content: [
        "Para acompañar este módulo, usa una hoja de seguimiento semanal, donde anotarás:",
        {
          type: "list",
          items: [
            "Gasto evitado",
            "Motivo del impulso",
            "Emoción presente",
            "Decisión final (¿lo compraste o no?)",
            "¿Qué aprendiste de ti ese día?",
          ],
        },
        "🔗 Puedes crearla fácilmente en una hoja de papel, en Google Sheets o con cualquier app de notas.",
        "También puedes usar el mismo formato para seguir hábitos positivos, como ahorrar, anotar gastos o no usar la tarjeta.",
      ],
    },
    {
      id: "ejemplo",
      navLabel: "Ejemplo real",
      title: "💬 Ejemplo real",
      content: [
        "🎓 Alondra, 19 años, de Guadalajara, se dio cuenta de que gastaba $150 a la semana en Uber cuando podía caminar o usar el camión.",
        "Decidió hacer el experimento de una “semana sin Uber innecesario” y logró ahorrar $600 ese mes.",
        "Con ese dinero, pagó su dominio web para lanzar un portafolio creativo como diseñadora.",
        "“No era dejar de vivir, era elegir mejor en qué gastaba lo que tanto me cuesta ganar.”",
      ],
    },
    {
      id: "maslow",
      navLabel: "Maslow",
      title: "🔺 Extra: La Pirámide de Maslow y nuestras decisiones de consumo",
      content: [
        {
          type: "paragraph",
          text: "📌 ¿Qué es la pirámide de Maslow?",
        },
        "Es un modelo psicológico que representa las necesidades humanas organizadas en niveles, desde las más básicas hasta las más complejas. Nos ayuda a entender por qué actuamos como actuamos, incluyendo cómo gastamos nuestro dinero.",
        {
          type: "table",
          headers: ["Nivel", "Necesidad humana", "Ejemplo relacionado"],
          rows: [
            [
              "1. Fisiológicas",
              "Comida, agua, descanso",
              "Comprar comida, transporte",
            ],
            [
              "2. Seguridad",
              "Estabilidad, salud, dinero",
              "Ahorro, seguros, renta",
            ],
            [
              "3. Sociales",
              "Pertenencia, amigos",
              "Ropa de marca, salidas, gadgets de moda",
            ],
            [
              "4. Reconocimiento",
              "Éxito, imagen, prestigio",
              "Accesorios, tecnología cara, estilo personal",
            ],
            [
              "5. Autorrealización",
              "Logros, propósito, libertad",
              "Educación, emprendimiento, experiencias",
            ],
          ],
        },
        {
          type: "paragraph",
          text: "🤔 ¿Qué tiene que ver esto con tus finanzas?",
        },
        {
          type: "paragraph",
          text: "¡Muchísimo! Muchas veces no gastamos por necesidad real, sino por:",
        },
        {
          type: "list",
          items: [
            "Sentir que “encajamos” (nivel 3)",
            "Mostrar éxito o autoestima (nivel 4)",
            "Tapar emociones o inseguridades",
          ],
        },
        {
          type: "paragraph",
          text: "💡 El consumo impulsivo muchas veces intenta llenar vacíos de niveles superiores sin cubrir bien los básicos.",
        },
      ],
    },
    {
      id: "evaluacion",
      navLabel: "Desafío final",
      title: "📝 Evaluación: Desafío final del curso",
      content: [
        'Desafío escrito: "Mi semana sin gastos innecesarios" (en tu cuaderno / documento)',
        "¿Qué debes hacer?",
        "Durante 7 días:",
        {
          type: "orderedList",
          items: [
            "Observa tus hábitos de consumo diario.",
            "Intenta evitar al menos un gasto innecesario por día.",
            "Registra tus decisiones (puede ser en una hoja, app o nota de voz).",
            "Reflexiona:",
          ],
        },
        {
          type: "list",
          items: [
            "¿Qué aprendiste sobre ti?",
            "¿Cuál fue el gasto más difícil de evitar?",
            "¿Te sentiste privado o empoderado?",
            "¿Cuánto lograste ahorrar?",
          ],
        },
      ],
    },
    {
      id: "bibliografia",
      navLabel: "Bibliografía",
      title: "📚 Bibliografía recomendada",
      content: [
        "Sofía Macías – “Pequeño Cerdo Capitalista: Finanzas personales para jóvenes”",
        "Daniel Kahneman – “Pensar rápido, pensar despacio”",
        "Blog: “Minimalismo Financiero” – finanzaszen.mx",
        "Podcast: “Neurona Financiera” – hábitos conscientes + dinero",
      ],
    },
    {
      id: "frase",
      navLabel: "Frase del día",
      title: "🧠 Frase del día",
      content: ["“No se trata de gastar menos. Se trata de gastar mejor.”"],
    },
  ],
};

export default modulo6;
